import type { Attachment } from '../../client'
import { humanToolName } from '../../lib/messageContent'
import type { Block } from '../../lib/inFlight'
import Attachments from './Attachments'
import ReasoningBlock, { type ReasoningPart } from './ReasoningBlock'
import StatusBubble from './StatusBubble'
import StreamingMarkdown from './StreamingMarkdown'

// Text that has arrived but not yet been revealed would render as an empty box.
function partsOf(block: Extract<Block, { kind: 'reasoning' }>): ReasoningPart[] {
  return block.events
    .filter((event) => event.type === 'tool_use' || event.text.length > 0)
    .map((event) =>
      event.type === 'tool_use'
        ? {
            kind: 'tool',
            toolUseId: event.toolUseId,
            label: event.displayName || humanToolName(event.toolName),
            running: event.status !== 'complete',
          }
        : { kind: 'text', text: event.text },
    )
}

/** A reasoning box keeps its spinner until the assistant starts replying below it. */
function stillThinking(blocks: Block[], index: number) {
  return !blocks.slice(index + 1).some((block) => block.kind === 'output')
}

export default function InFlightMessage({
  blocks,
  onPreview,
}: {
  blocks: Block[]
  onPreview: (attachment: Attachment) => void
}) {
  if (blocks.length === 0) return <StatusBubble label="Thinking" />

  return (
    <div className="flex w-full flex-col items-start gap-2">
      {blocks.map((block, index) => {
        if (block.kind === 'reasoning') {
          const parts = partsOf(block)
          if (parts.length === 0) return null
          return <ReasoningBlock key={index} parts={parts} streaming={stillThinking(blocks, index)} />
        }

        if (block.kind === 'output') return <StreamingMarkdown key={index} text={block.text} />

        return <Attachments key={index} items={block.attachments} onPreview={onPreview} />
      })}
    </div>
  )
}
